import { ReactNode, useMemo, useState, type ComponentType } from 'react';
import { swallowEvent, titleCase } from '@admin-panel/utils';
import { ChatCircleTextIcon } from '@phosphor-icons/react';
import { Button, Drawer, Tabs, Typography, type ButtonProps } from 'antd';
import { CommentsList } from './CommentsList';
import styled from '@emotion/styled';

const StyledTabs = styled(Tabs)`
  height: 100%;

  .ant-tabs-content,
  .ant-tabs-tabpane {
    height: 100%;
  }

  div[role='tabpanel'] {
    padding: 0 !important;
  }

  .ant-tabs-tab {
    padding: 0 !important;
  }
  .ant-tabs-tab-active {
    background-color: aliceblue;
  }
`;

type CommentEntityName = 'order' | 'package' | 'orderlineitem';

interface ITabbedEntity {
  name: CommentEntityName;
  id: string;
  renderLabel?: () => ReactNode;
}

interface ITabbedCommentsDrawerProps {
  isDisabled?: boolean;
  entities: Array<ITabbedEntity>;
  triggerType?: 'button' | 'icon';
  block?: boolean;
  triggerText?: string;
  triggerButtonProps?: ButtonProps;
  triggerButtonComponent?: ComponentType<ButtonProps>;
}

export const TabbedCommentsDrawer = ({
  entities,
  isDisabled = false,
  triggerType = 'icon',
  block = false,
  triggerText = 'Comments',
  triggerButtonProps,
  triggerButtonComponent,
}: ITabbedCommentsDrawerProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const TriggerButton = triggerButtonComponent ?? Button;

  const tabItems = useMemo(
    () =>
      entities.map(entity => ({
        key: entity.id,
        children: (
          <div
            style={{
              height: '100%',
            }}
          >
            <CommentsList entityId={entity.id} entityName={entity.name} />
          </div>
        ),
        label: entity.renderLabel ? (
          entity.renderLabel()
        ) : (
          <Typography.Text strong>{`${entity.name}#${entity.id}`}</Typography.Text>
        ),
      })),
    [entities],
  );

  return (
    <>
      {triggerType === 'icon' ? (
        <TriggerButton
          shape="circle"
          icon={<ChatCircleTextIcon />}
          size="small"
          onClick={e => {
            setOpen(true);
            swallowEvent(e);
          }}
          disabled={isDisabled}
        />
      ) : (
        <TriggerButton
          {...{
            ...triggerButtonProps,
            type: triggerButtonProps?.type ?? 'primary',
            icon:
              triggerButtonProps &&
              Object.prototype.hasOwnProperty.call(triggerButtonProps, 'icon') ? (
                triggerButtonProps.icon
              ) : (
                <ChatCircleTextIcon />
              ),
            block: triggerButtonProps?.block ?? block,
          }}
          onClick={e => {
            setOpen(true);
            swallowEvent(e);
          }}
          disabled={isDisabled || triggerButtonProps?.disabled}
        >
          {triggerButtonProps?.children ?? triggerText}
        </TriggerButton>
      )}
      <Drawer
        closable
        destroyOnHidden
        width="90vw"
        title={
          <div style={{ width: '100%', textAlign: 'center' }}>
            {titleCase(entities[0]?.name)} Comments
          </div>
        }
        styles={{
          body: {
            padding: 0,
          },
        }}
        placement="right"
        open={open}
        onClose={() => setOpen(false)}
      >
        <StyledTabs tabPosition="left" items={tabItems} animated />
      </Drawer>
    </>
  );
};

export default TabbedCommentsDrawer;
